// Copyright ©️ 2026 eVolve MEP, LLC

import type { HexTriplet, ProductId } from '@evolvemep/api-ts';
import { type MantineSize, ThemeIcon, getSize } from '@mantine/core';

import { EvolveIcon, type EvolveIconName } from 'assets/icons/EvolveIcon';
import { PRODUCT_IDS } from 'constants/globalConstants';
import { EVOLVEICON_SIZE_PREFIX } from 'styles/theme/variableResolver';

const licenseIconLookup: Record<ProductId, { icon: EvolveIconName; color: HexTriplet }> = {
  [PRODUCT_IDS.MECHANICAL_LICENSE]: {
    icon: 'ProductMechanical',
    color: '#1DC389' as HexTriplet,
  },
  [PRODUCT_IDS.ELECTRICAL_LICENSE]: {
    icon: 'ProductElectrical',
    color: '#E00052' as HexTriplet,
  },
  [PRODUCT_IDS.SHOP_LICENSE]: {
    icon: 'ProductShop',
    color: '#9747FF' as HexTriplet,
  },
  [PRODUCT_IDS.FABRICATION_LICENSE]: {
    icon: 'ProductFabrication',
    color: '#6161FF' as HexTriplet,
  },
  [PRODUCT_IDS.WEB_SPOOLING_LICENSE]: {
    icon: 'Spools',
    color: '#FF473B' as HexTriplet,
  },
  [PRODUCT_IDS.SYNC_DESKTOP_LICENSE]: {
    icon: 'ProductSync',
    color: '#00A5B7' as HexTriplet,
  },
  [PRODUCT_IDS.REVIT_ROLES]: {
    icon: 'ProductRevitRoles',
    color: '#FF5A27' as HexTriplet,
  },
  [PRODUCT_IDS.MATERIAL_ANALYTICS]: {
    icon: 'ProductMaterialAnalytics',
    color: '#175E93' as HexTriplet,
  },
  [PRODUCT_IDS.REVIT_USAGE_HISTORY]: {
    icon: 'ProductUsageAnalytics',
    color: '#FFA82D' as HexTriplet,
  },
};

const fillerIconProps: { icon: EvolveIconName; color: HexTriplet } = {
  icon: 'Info',
  color: '#00C3C3' as HexTriplet,
};

type Props = {
  /** @defaultValue 'xl' */
  size?: MantineSize;
} & (
  | {
      productId: ProductId;
    }
  | {
      color: HexTriplet;
      icon: EvolveIconName;
    }
);

export const getLicenseIconSize = (size: MantineSize) => `calc(${getSize(size, EVOLVEICON_SIZE_PREFIX, false)} + 24px)`;

/**
 * An icon representing a specific product, or a feature we want top represent as a product.
 * If a product ID is given as a prop, the icon and color will be automatically picked.
 * If you want to use this component for a feature we're representing as a product (such as SSO),
 * provide the icon and color as props.
 */
export const LicenseIcon = ({ size = 'xl', ...props }: Props) => {
  const { color, icon } = 'productId' in props ? (licenseIconLookup[props.productId] ?? fillerIconProps) : props;
  return (
    <ThemeIcon color={color} size={getLicenseIconSize(size)}>
      <EvolveIcon size={size} icon={icon} color="white" />
    </ThemeIcon>
  );
};
